import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiResponse, BreadcrumbService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { BusinessEntityType } from '../../models/finance/business';
import {
  AdjustmentType,
  InventoryAdjustmentDto,
  InventoryAdjustmentInsert,
  InventoryAdjustmentLineDto,
  InventoryAdjustmentStatus,
  LotBalanceSummary,
  LotBasedAdjustmentRequest,
  LotBasedAdjustmentResponse,
  UnitOfMeasure,
} from '../../models/inventory/inventory-adjustment';
import { LotItem, LotItemInsert } from '../../models/inventory/lot-item';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../models/inventory/lot-item-search';
import { LocationNode } from '../../models/location';
import { BusinessProxyService } from '../../proxies/finance/business.proxy.service';
import { InventoryAdjustmentProxyService } from '../../proxies/inventory/inventory-adjustment.proxy.service';
import { LocationProxyService } from '../../proxies/inventory/location.proxy.service';
import { LotItemProxyService } from '../../proxies/inventory/lot-item.proxy.service';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';

interface AdjustmentLine {
  id: string;
  lotItem: LotItem | null;
  lotItemNumber: string;
  fromLocationCode: string;
  toLocationCode: string;
  quantityAdjustment: number | null;
  uom: UnitOfMeasure;
  comments: string;
}

@Component({
  selector: 'app-inventory-adjustment-details',
  templateUrl: './inventory-adjustment-details.component.html',
  styleUrl: './inventory-adjustment-details.component.scss',
})
export class InventoryAdjustmentDetailsComponent implements OnInit {
  isLoading = signal<boolean>(false);
  adjustment: InventoryAdjustmentDto | null = null;

  isCreateMode = false;
  isLotItemMode = false;
  isLotReductionMode = false;
  isSearching = false;

  lotItemSuggestions: LotItemSearchResultItem[] = [];
  lines: AdjustmentLine[] = [];

  locationOptions: { label: string; value: string }[] = [];
  manufacturerOptions: { label: string; value: string }[] = [];

  sourceLocationOptions = [
    { label: 'Normal Warehouse (NT.WH-RM-1)', value: 'NT.WH-RM-1' },
    { label: 'SIMTEX Warehouse (SIMTEX.WH-RM-2)', value: 'SIMTEX.WH-RM-2' },
    { label: 'Initial Virtual Warehouse (NT.INITIAL-WH-RM-1)', value: 'NT.INITIAL-WH-RM-1' },
    { label: 'Knitting Work Area (NT.WA-KNITTING-1)', value: 'NT.WA-KNITTING-1' },
    { label: 'SIMTEX Knitting Work Area (SIMTEX.WA-KNITTING-2)', value: 'SIMTEX.WA-KNITTING-2' },
    // I need add more predefined locations as needed, como Nelsno diga
  ];

  lotBalance: LotBalanceSummary | null = null;

  adjustmentForm = new FormGroup({
    type: new FormControl<AdjustmentType>(AdjustmentType.QuantityCorrection, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  lotItemForm = new FormGroup({
    providerLotId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    qty: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0.01)],
    }),
    itemCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    manufacturerReference: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    currentLocationCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    packagingType: new FormControl<number>(1, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notes: new FormControl<string>('', {
      nonNullable: true,
    }),
  });

  lotReductionForm = new FormGroup({
    providerLotId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    reductionAmount: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0.01)],
    }),
    locationCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    uom: new FormControl<UnitOfMeasure>(UnitOfMeasure.lb, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    comments: new FormControl<string>('', {
      nonNullable: true,
    }),
  });

  typeOptions = [
    { label: 'Location Transfer', value: AdjustmentType.LocationTransfer },
    { label: 'Quantity Correction', value: AdjustmentType.QuantityCorrection },
    { label: 'Location & Quantity', value: AdjustmentType.LocationAndQuantity },
  ];

  uomOptions = [
    { label: 'Pounds (lb)', value: UnitOfMeasure.lb },
    { label: 'Kilograms (kg)', value: UnitOfMeasure.kg },
    { label: 'Ounces (oz)', value: UnitOfMeasure.oz },
    { label: 'Grams (g)', value: UnitOfMeasure.g },
    { label: 'Yards (yd)', value: UnitOfMeasure.yd },
    { label: 'Meters (m)', value: UnitOfMeasure.m },
    { label: 'Feet (ft)', value: UnitOfMeasure.ft },
    { label: 'Unit', value: UnitOfMeasure.unit },
  ];

  typeLabels = {
    [AdjustmentType.LocationTransfer]: 'Location Transfer',
    [AdjustmentType.QuantityCorrection]: 'Quantity Correction',
    [AdjustmentType.LocationAndQuantity]: 'Location & Quantity',
  };

  readonly AdjustmentType = AdjustmentType;

  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly translateService = inject(TranslateService);
  constructor(
    private inventoryAdjustmentProxyService: InventoryAdjustmentProxyService,
    private lotItemProxyService: LotItemProxyService,
    private locationProxyService: LocationProxyService,
    private businessProxyService: BusinessProxyService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadDropdownData();
    this.setupFormListeners();

    this.route.queryParams.subscribe((params) => {
      const id = params['id'];
      const type = params['type'];
      if (id) {
        if (id === 'new') {
          this.isLotItemMode = type === 'lot-item';
          this.isLotReductionMode = type === 'lot-reduction';
          this.initializeCreateMode();
        } else {
          this.loadAdjustment(id);
        }
      } else {
        GlobalMessageUtil.error('Error', 'No adjustment ID provided');
        this.router.navigate(['/inventory-adjustment']);
      }
    });
  }

  initializeCreateMode(): void {
    this.isCreateMode = true;
    this.breadcrumbService.setCurrent('Common_New');
    this.addLine();
  }

  loadAdjustment(id: string): void {
    this.isLoading.set(true);
    this.inventoryAdjustmentProxyService
      .getById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<InventoryAdjustmentDto>) => {
          if (response.success && response.data) {
            this.adjustment = response.data;
            this.breadcrumbService.setCurrent(this.adjustment.number);
          } else {
            GlobalMessageUtil.error('Error', 'Failed to load adjustment');
            this.router.navigate(['/inventory-adjustment']);
          }
        },
        error: () => {
          this.router.navigate(['/inventory-adjustment']);
        },
      });
  }

  voidAdjustment(): void {
    if (!this.adjustment || !this.adjustment.id) return;

    this.confirmationService.confirm({
      key: 'main',
      message: `Are you sure you want to void adjustment ${this.adjustment.number}? This action cannot be undone and will NOT reverse inventory changes.`,
      header: 'Confirm Void',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isLoading.set(true);
        this.inventoryAdjustmentProxyService
          .void(this.adjustment!.id!)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (response: ApiResponse<InventoryAdjustmentDto>) => {
              if (response.success) {
                GlobalMessageUtil.success('Success', 'Adjustment voided successfully');
                this.adjustment = response.data ?? null;
              } else {
                const errorMsg = getLocalizedValue(this.translateService, response.errorMessage) || 'Failed to void adjustment';
                GlobalMessageUtil.error('Error', errorMsg);
              }
            },
          });
      },
    });
  }

  back(): void {
    this.router.navigate(['/inventory-adjustment']);
  }

  getStatusSeverity(status: InventoryAdjustmentStatus | string): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' {
    // Convert string status to number if needed (for backwards compatibility)
    let statusValue: number;
    if (typeof status === 'string') {
      statusValue = InventoryAdjustmentStatus[status as keyof typeof InventoryAdjustmentStatus] as number;
    } else {
      statusValue = status;
    }

    switch (statusValue) {
      case InventoryAdjustmentStatus.Completed:
        return 'success';
      case InventoryAdjustmentStatus.Voided:
        return 'danger';
      case InventoryAdjustmentStatus.Draft:
        return 'warn';
      default:
        return 'info';
    }
  }

  getTypeLabel(type: AdjustmentType): string {
    return this.typeLabels[type] || AdjustmentType[type];
  }

  getStatusLabel(status: InventoryAdjustmentStatus): string {
    return InventoryAdjustmentStatus[status];
  }

  getUomLabel(uom: UnitOfMeasure): string {
    return UnitOfMeasure[uom];
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleString();
  }

  canVoid(): boolean {
    return this.adjustment?.status === InventoryAdjustmentStatus.Completed;
  }

  addLine(): void {
    const newLine: AdjustmentLine = {
      id: this.generateId(),
      lotItem: null,
      lotItemNumber: '',
      fromLocationCode: '',
      toLocationCode: '',
      quantityAdjustment: null,
      uom: UnitOfMeasure.lb,
      comments: '',
    };
    this.lines.push(newLine);
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) {
      this.lines.splice(index, 1);
    }
  }

  searchLotItems(event: { query: string }): void {
    const query = event.query;
    if (!query || query.length < 2) {
      this.lotItemSuggestions = [];
      return;
    }

    const command: LotItemSearchCommand = {
      lotItemNumbers: [query],
    };

    this.lotItemProxyService.search(command).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.lotItemSuggestions = response.data.items.map((lotItem: LotItemSearchResultItem) => ({
            ...lotItem,
            fieldToDisplay: `(${lotItem.number}) - ${lotItem.itemCode} - Qty: ${lotItem.currentQuantity}`,
          })) as unknown as LotItemSearchResultItem[];
        } else {
          this.lotItemSuggestions = [];
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error searching lot items:', error);
        this.lotItemSuggestions = [];
      },
    });
  }

  searchLotItemByNumber(line: AdjustmentLine): void {
    if (!line.lotItemNumber || line.lotItemNumber.trim().length === 0) {
      GlobalMessageUtil.warn('Warning', 'Please enter a lot item number');
      return;
    }

    this.isSearching = true;

    const command: LotItemSearchCommand = {
      lotItemNumbers: [line.lotItemNumber.trim()],
    };

    this.lotItemProxyService
      .search(command)
      .pipe(finalize(() => (this.isSearching = false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.items && response.data.items.length > 0) {
            const lotItem = {
              ...response.data.items[0],
              netQuantity: response.data.items[0].currentQuantity,
            } as unknown as LotItem;
            this.onLotItemSelect(line, lotItem);
            GlobalMessageUtil.success('Success', `Lot item found: ${lotItem.itemCode} - Qty: ${lotItem.netQuantity}`);
          } else {
            GlobalMessageUtil.warn('Warning', 'Lot item not found');
            this.onLotItemClear(line);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error searching lot item:', error);
        },
      });
  }

  onLotItemSelect(line: AdjustmentLine, event: LotItem): void {
    if (event) {
      line.lotItem = event;
      line.lotItemNumber = event.number || '';

      if (event.currentLocationCode) {
        // Check if the current location code matches one of the predefined options
        const matchingOption = this.sourceLocationOptions.find((option) => option.value === event.currentLocationCode);

        // Use the location code if it matches a predefined option, otherwise leave empty
        line.fromLocationCode = matchingOption ? event.currentLocationCode : '';
      }
    }
  }

  onLotItemClear(line: AdjustmentLine): void {
    line.lotItem = null;
    line.lotItemNumber = '';
    line.fromLocationCode = '';
  }

  calculateNewQuantity(line: AdjustmentLine): number {
    if (!line.lotItem) return 0;
    const currentQty = line.lotItem.netQuantity || 0;
    const adjustment = line.quantityAdjustment || 0;
    return currentQty + adjustment;
  }

  isLineValid(line: AdjustmentLine): boolean {
    const hasLotItem = !!line.lotItemNumber;
    const hasQuantityAdjustment = line.quantityAdjustment !== null && line.quantityAdjustment !== 0;
    const hasLocationTransfer = !!line.toLocationCode;

    if (!hasLotItem) return false;

    const type = this.adjustmentForm.value.type;
    if (type === AdjustmentType.LocationTransfer) {
      return hasLocationTransfer;
    } else if (type === AdjustmentType.QuantityCorrection) {
      return hasQuantityAdjustment;
    } else if (type === AdjustmentType.LocationAndQuantity) {
      return hasLocationTransfer && hasQuantityAdjustment;
    }

    return false;
  }

  get isFormValid(): boolean {
    if (!this.adjustmentForm.valid) return false;
    if (this.lines.length === 0) return false;
    return this.lines.every((line) => this.isLineValid(line));
  }

  submit(): void {
    if (!this.isFormValid) {
      GlobalMessageUtil.warn('Warning', 'Please fill all required fields correctly');
      return;
    }

    const adjustmentType = this.adjustmentForm.value.type!;
    const adjustmentTypeLabel = this.typeLabels[adjustmentType];
    const lineCount = this.lines.length;
    const reason = this.adjustmentForm.value.reason!;

    // Determine what columns to show based on adjustment type
    const showLocation = adjustmentType === AdjustmentType.LocationTransfer || adjustmentType === AdjustmentType.LocationAndQuantity;
    const showQuantity = adjustmentType === AdjustmentType.QuantityCorrection || adjustmentType === AdjustmentType.LocationAndQuantity;

    // Build action description
    let actionDescription = '';
    if (adjustmentType === AdjustmentType.LocationTransfer) {
      actionDescription = 'The following lot items will be <strong>transferred to new locations</strong>:';
    } else if (adjustmentType === AdjustmentType.QuantityCorrection) {
      actionDescription = 'The following lot items will have <strong>quantity adjustments</strong>:';
    } else {
      actionDescription = 'The following lot items will be <strong>transferred and have quantity adjustments</strong>:';
    }

    // Build table headers dynamically
    let tableHeaders =
      '<th style="padding: 10px 12px; text-align: left; border-bottom: 2px solid #cbd5e0; font-weight: 600;">Lot Item #</th>';
    if (showLocation) {
      tableHeaders +=
        '<th style="padding: 10px 12px; text-align: left; border-bottom: 2px solid #cbd5e0; font-weight: 600;">Location Change</th>';
    }
    if (showQuantity) {
      tableHeaders +=
        '<th style="padding: 10px 12px; text-align: right; border-bottom: 2px solid #cbd5e0; font-weight: 600;">Adjustment</th>';
    }

    // Build table rows with only relevant data
    const linesTableRows = this.lines
      .map((line) => {
        let rowCells = `<td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0;"><strong>${line.lotItemNumber}</strong></td>`;

        if (showLocation) {
          let locationChange = '';
          if (line.fromLocationCode && line.toLocationCode) {
            locationChange = `<span style="color: #dc2626;">${line.fromLocationCode}</span> → <span style="color: #16a34a;">${line.toLocationCode}</span>`;
          } else if (line.toLocationCode) {
            locationChange = `→ <span style="color: #16a34a;">${line.toLocationCode}</span>`;
          } else {
            locationChange = '<span style="color: #9ca3af;">No change</span>';
          }
          rowCells += `<td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0;">${locationChange}</td>`;
        }

        if (showQuantity) {
          const qtyValue = line.quantityAdjustment !== null && line.quantityAdjustment !== 0 ? line.quantityAdjustment : 0;
          const qtyColor = qtyValue > 0 ? '#16a34a' : qtyValue < 0 ? '#dc2626' : '#9ca3af';
          const qtySign = qtyValue > 0 ? '+' : '';
          rowCells += `<td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: right;"><strong style="color: ${qtyColor};">${qtySign}${qtyValue} ${this.getUomLabel(line.uom)}</strong></td>`;
        }

        return `<tr>${rowCells}</tr>`;
      })
      .join('');

    const summaryHtml = `
      <div style="text-align: left; max-width: 700px;">
        <div style="background-color: #f0f9ff; border-left: 4px solid #3b82f6; padding: 12px 16px; margin-bottom: 20px; border-radius: 4px;">
          <div style="font-weight: 600; color: #1e40af; margin-bottom: 4px;">Adjustment Type: ${adjustmentTypeLabel}</div>
          <div style="color: #64748b; font-size: 0.875rem;">Reason: ${reason}</div>
        </div>

        <p style="margin-bottom: 12px; color: #475569; font-size: 0.95rem;">${actionDescription}</p>

        <div style="max-height: 350px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 6px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
            <thead style="background-color: #f8fafc; position: sticky; top: 0; z-index: 1;">
              <tr>${tableHeaders}</tr>
            </thead>
            <tbody>
              ${linesTableRows}
            </tbody>
          </table>
        </div>

        <div style="margin-top: 16px; padding: 12px; background-color: #fef3c7; border-radius: 4px; border-left: 4px solid #f59e0b;">
          <strong style="color: #92400e;">⚠ Note:</strong>
          <span style="color: #78350f; font-size: 0.875rem;">This will create a Draft adjustment affecting <strong>${lineCount} lot item${lineCount !== 1 ? 's' : ''}</strong>.</span>
        </div>
      </div>
    `;

    this.confirmationService.confirm({
      key: 'main',
      message: summaryHtml,
      header: 'Review Inventory Adjustment',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Create Adjustment',
      rejectLabel: 'Cancel',
      accept: () => {
        this.executeSubmit();
      },
    });
  }

  private executeSubmit(): void {
    this.isLoading.set(true);

    const adjustmentLines: InventoryAdjustmentLineDto[] = this.lines.map((line) => ({
      lineId: line.id,
      lotItemNumber: line.lotItemNumber,
      fromLocationCode: line.fromLocationCode || undefined,
      toLocationCode: line.toLocationCode || undefined,
      quantityAdjustment: line.quantityAdjustment || undefined,
      uom: line.uom,
      comments: line.comments || undefined,
    }));

    const command: InventoryAdjustmentInsert = {
      status: InventoryAdjustmentStatus.Draft,
      type: this.adjustmentForm.value.type!,
      reason: this.adjustmentForm.value.reason!,
      lines: adjustmentLines,
    };

    this.inventoryAdjustmentProxyService
      .create(command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<InventoryAdjustmentDto>) => {
          if (response.success) {
            GlobalMessageUtil.success('Success', 'Inventory adjustment created successfully');
            this.router.navigate(['/inventory-adjustment']);
          } else {
            const errorMsg = getLocalizedValue(this.translateService, response.errorMessage) || 'Failed to create adjustment';
            GlobalMessageUtil.error('Error', errorMsg);
          }
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/inventory-adjustment']);
  }

  private generateId(): string {
    // Generate a MongoDB ObjectId-like string (24 hex characters)
    const timestamp = Math.floor(Date.now() / 1000).toString(16);
    const machineId = Math.floor(Math.random() * 16777216)
      .toString(16)
      .padStart(6, '0');
    const processId = Math.floor(Math.random() * 65536)
      .toString(16)
      .padStart(4, '0');
    const counter = Math.floor(Math.random() * 16777216)
      .toString(16)
      .padStart(6, '0');
    return (timestamp + machineId + processId + counter).padStart(24, '0');
  }

  get selectedType(): AdjustmentType {
    return this.adjustmentForm.value.type || AdjustmentType.QuantityCorrection;
  }

  get showQuantityFields(): boolean {
    const type = this.selectedType;
    return type === AdjustmentType.QuantityCorrection || type === AdjustmentType.LocationAndQuantity;
  }

  get showLocationFields(): boolean {
    const type = this.selectedType;
    return type === AdjustmentType.LocationTransfer || type === AdjustmentType.LocationAndQuantity;
  }

  get isLotItemFormValid(): boolean {
    return this.lotItemForm.valid;
  }

  get isLotReductionFormValid(): boolean {
    return this.lotReductionForm.valid;
  }

  submitLotReduction(): void {
    if (!this.isLotReductionFormValid) {
      GlobalMessageUtil.warn('Warning', 'Please fill all required fields correctly');
      return;
    }

    const formValue = this.lotReductionForm.getRawValue();

    const summaryHtml = `
      <div style="text-align: left; max-width: 550px;">
        <div style="background-color: #fef2f2; border-left: 4px solid #dc2626; padding: 12px 16px; margin-bottom: 20px; border-radius: 4px;">
          <div style="font-weight: 600; color: #991b1b; margin-bottom: 4px;">Lot Reduction</div>
          <div style="color: #64748b; font-size: 0.875rem;">This will reduce inventory using FIFO method</div>
        </div>

        <div style="background-color: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
          <div style="margin-bottom: 12px;">
            <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Lot ID</div>
            <div style="font-size: 1.1rem; font-weight: 600; color: #1e293b;">${formValue.providerLotId}</div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
            <div>
              <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Location</div>
              <div style="font-weight: 600; color: #1e293b;">${formValue.locationCode}</div>
            </div>
            <div>
              <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Amount to Reduce</div>
              <div style="font-size: 1.25rem; font-weight: 700; color: #dc2626;">-${formValue.reductionAmount} ${this.getUomLabel(formValue.uom!)}</div>
            </div>
          </div>

          <div style="border-top: 1px solid #e2e8f0; padding-top: 12px;">
            <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Reason</div>
            <div style="color: #475569;">${formValue.reason}</div>
          </div>

          ${
            formValue.comments
              ? `
          <div style="border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 12px;">
            <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Comments</div>
            <div style="color: #475569; font-style: italic;">${formValue.comments}</div>
          </div>
          `
              : ''
          }
        </div>

        <div style="padding: 12px; background-color: #fef3c7; border-radius: 4px; border-left: 4px solid #f59e0b;">
          <strong style="color: #92400e;">⚠ Note:</strong>
          <span style="color: #78350f; font-size: 0.875rem;">Lot items will be reduced using FIFO (First-In, First-Out) method.</span>
        </div>
      </div>
    `;

    this.confirmationService.confirm({
      key: 'main',
      message: summaryHtml,
      header: 'Review Lot-Based Reduction',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Reduce Inventory',
      rejectLabel: 'Cancel',
      accept: () => {
        this.executeLotReduction();
      },
    });
  }

  private executeLotReduction(): void {
    const formValue = this.lotReductionForm.getRawValue();

    const request: LotBasedAdjustmentRequest = {
      providerLotId: formValue.providerLotId!,
      reductionAmount: formValue.reductionAmount!,
      locationCode: formValue.locationCode!,
      reason: formValue.reason!,
      uom: formValue.uom!,
      comments: formValue.comments || undefined,
    };

    this.isLoading.set(true);

    this.inventoryAdjustmentProxyService
      .createLotBasedReduction(request)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<LotBasedAdjustmentResponse>) => {
          if (response.success && response.data) {
            GlobalMessageUtil.success(
              'Success',
              `Lot reduction completed. Total reduced: ${response.data.totalReduced} ${response.data.uom}`,
            );
            this.router.navigate(['/inventory-adjustment']);
          } else {
            const errorMsg = getLocalizedValue(this.translateService, response.errorMessage) || 'Failed to create lot reduction';
            GlobalMessageUtil.error('Error', errorMsg);
          }
        },
      });
  }

  submitLotItem(): void {
    if (!this.isLotItemFormValid) {
      GlobalMessageUtil.warn('Warning', 'Please fill all required fields correctly');
      return;
    }

    const formValue = this.lotItemForm.getRawValue();

    const summaryHtml = `
      <div style="text-align: left; max-width: 550px;">
        <div style="background-color: #f0fdf4; border-left: 4px solid #16a34a; padding: 12px 16px; margin-bottom: 20px; border-radius: 4px;">
          <div style="font-weight: 600; color: #166534; margin-bottom: 4px;">New Lot Item</div>
          <div style="color: #64748b; font-size: 0.875rem;">This will add a new lot item to inventory</div>
        </div>

        <div style="background-color: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
            <div>
              <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Lot ID</div>
              <div style="font-size: 1.1rem; font-weight: 600; color: #1e293b;">${formValue.providerLotId}</div>
            </div>
            <div>
              <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Quantity</div>
              <div style="font-size: 1.25rem; font-weight: 700; color: #16a34a;">+${formValue.qty}</div>
            </div>
          </div>

          <div style="border-top: 1px solid #e2e8f0; padding-top: 12px; margin-bottom: 12px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Item Code</div>
                <div style="font-weight: 600; color: #1e293b;">${formValue.itemCode}</div>
              </div>
              <div>
                <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Location</div>
                <div style="font-weight: 600; color: #1e293b;">${formValue.currentLocationCode}</div>
              </div>
            </div>
          </div>

          <div style="border-top: 1px solid #e2e8f0; padding-top: 12px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Manufacturer</div>
                <div style="color: #475569;">${formValue.manufacturerReference}</div>
              </div>
              <div>
                <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Packaging Type</div>
                <div style="color: #475569;">${formValue.packagingType}</div>
              </div>
            </div>
          </div>

          ${
            formValue.notes
              ? `
          <div style="border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 12px;">
            <div style="color: #64748b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Notes</div>
            <div style="color: #475569; font-style: italic;">${formValue.notes}</div>
          </div>
          `
              : ''
          }
        </div>

        <div style="padding: 12px; background-color: #dbeafe; border-radius: 4px; border-left: 4px solid #3b82f6;">
          <strong style="color: #1e40af;">ℹ Info:</strong>
          <span style="color: #1e3a8a; font-size: 0.875rem;">The lot item will be created as active in the specified location.</span>
        </div>
      </div>
    `;

    this.confirmationService.confirm({
      key: 'main',
      message: summaryHtml,
      header: 'Review New Lot Item',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Create Lot Item',
      rejectLabel: 'Cancel',
      accept: () => {
        this.executeLotItem();
      },
    });
  }

  private executeLotItem(): void {
    const formValue = this.lotItemForm.getRawValue();

    const lotItemInsert: LotItemInsert = {
      providerLotId: formValue.providerLotId!,
      netQuantity: formValue.qty!,
      grossQuantity: formValue.qty!,
      itemCode: formValue.itemCode!,
      manufacturerReference: formValue.manufacturerReference!,
      currentLocationCode: formValue.currentLocationCode!,
      isActive: true,
      packagingType: formValue.packagingType!,
      notes: formValue.notes || undefined,
    };

    this.isLoading.set(true);

    this.lotItemProxyService
      .createFromAdjustment(lotItemInsert)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<LotItem>) => {
          if (response.success) {
            GlobalMessageUtil.success('Success', 'Lot item created successfully');
            this.router.navigate(['/inventory-adjustment']);
          } else {
            const errorMsg = getLocalizedValue(this.translateService, response.errorMessage) || 'Failed to create lot item';
            GlobalMessageUtil.error('Error', errorMsg);
          }
        },
      });
  }

  private loadDropdownData(): void {
    // Load locations
    this.locationProxyService.getAll().subscribe({
      next: (response: ApiResponse<LocationNode[]>) => {
        if (response.success && response.data) {
          this.locationOptions = response.data.map((loc) => ({
            label: `${loc.code} - ${loc.description || ''}`.trim(),
            value: loc.code,
          }));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading locations:', error);
      },
    });

    // Load manufacturers/businesses
    this.businessProxyService.search({ businessTypes: [BusinessEntityType.Manufacturer] }, 1, 200).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.manufacturerOptions = response.data.items.map((biz) => ({
            label: biz.name,
            value: biz.id || '',
          }));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading manufacturers:', error);
      },
    });
  }

  private setupFormListeners(): void {
    // Listen for providerLotId changes in lot reduction form
    this.lotReductionForm.controls.providerLotId.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((providerLotId) => {
      if (providerLotId) {
        this.fetchLotBalance(providerLotId);
      } else {
        this.lotBalance = null;
      }
    });

    // Listen for location changes to update balance
    this.lotReductionForm.controls.locationCode.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((locationCode) => {
      const providerLotId = this.lotReductionForm.controls.providerLotId.value;
      if (providerLotId && locationCode) {
        this.fetchLotBalance(providerLotId, locationCode);
      }
    });
  }

  private fetchLotBalance(providerLotId: string, locationCode?: string): void {
    this.inventoryAdjustmentProxyService.getLotBalance(providerLotId, locationCode || undefined).subscribe({
      next: (response: ApiResponse<LotBalanceSummary>) => {
        if (response.success && response.data) {
          this.lotBalance = response.data;
        } else {
          this.lotBalance = null;
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error fetching lot balance:', error);
        this.lotBalance = null;
      },
    });
  }
}
